"use client";

import { useHistory, useLocation } from "react-router-dom";

import { Button } from "@/components/ui/button-tw";
import { Checkbox } from "@/components/ui/check-box";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import {
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ChevronDown,
  Filter,
  Plus,
  Banknote,
  Download,
  Send,
  Loader2,
} from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { cn } from "@/utils/classnames";
import { useInvoices } from "@/hooks/invoice/use-invoices";
import { addresseeNamePriority } from "@/utils/invoice";
import dayjs from "dayjs";
import { InvoiceStatusBadge } from "./invoice-status-badge";
import { useInvoiceDrawer } from "@/hooks/invoice/use-invoice-drawer";
import { useQuickPayViewDrawer } from "@/hooks/invoice/use-quickpay-view-drawer";
import { useQuickPayCreateDrawer } from "@/hooks/invoice/use-quickpay-create-drawer";
import { Calendar } from "@/components/ui/calendar";
import {
  endOfMonth,
  endOfYear,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from "date-fns";
import { Slider } from "@/components/ui/slider";
import { hasPermission } from "@/utils/auth";
import { useAuth } from "@/providers/auth";
import { useCheckDepositDrawer } from "@/hooks/invoice/use-check-deposit-drawer";
import { exportToCsv } from "@/scripts/functions";
import { UploadInvoiceButton } from "./upload-invoice-button";

import TablePagination from "@/components/table-pagination";
import MainTable from "@/components/main-table";
import DeleteInvoiceAlert from "./delete-invoice-alert";
import { CancelPaymentModal } from "./cancel-payment-modal";
import { PENDING_INVOICE_STATUS, PROCESSING_INVOICE_STATUS } from "@/constants/invoice";
import TableColumnsManager from "@/components/table-columns-manager";
import SearchInput from "@/components/search-input";
import QuickPayDrawer from "./quick-pay-drawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useBulkSend } from "@/hooks/invoice/use-bulk-send";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { invoiceQueryKeys } from "@/queries/invoice";
import { AxiosError } from "axios";

const formatLink = (link) => {
  try {
    new URL(link);
    return link;
  } catch (e) {
    return `https://${link}`;
  }
};

// Custom filter function for multi-column searching
const multiColumnFilterFn = (row, _columnId, filterValue) => {
  const info = row.original.invoice_info;
  const searchableRowContent = `${
    info?.invoice_to?.first_name || ""
  } ${info?.invoice_to?.last_name || ""} ${
    info?.invoice_to?.email || ""
  } ${info?.invoice_to?.business_name || ""} ${
    info?.invoice_to?.business_dba || ""
  } ${info?.invoice_number || ""} ${
    info?.invoice_total != null
      ? `${info.invoice_total} ${Number(info.invoice_total).toFixed(2)} ${Number(info.invoice_total).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : ""
  }`.toLowerCase();
  const searchTerm = (filterValue ?? "").toLowerCase();
  return searchableRowContent.includes(searchTerm);
};

const statusFilterFn = (row, columnId, filterValue) => {
  if (!filterValue?.length) return true;
  const status = row.getValue(columnId);
  return filterValue.includes(status);
};

const amountFilterFn = (row, columnId, filterValue) => {
  if (!filterValue?.length) return true;
  const amount = row.getValue(columnId);
  return filterValue[0] <= amount && filterValue[1] >= amount;
};

const dateCreatedFilterFn = (row, columnId, filterValue) => {
  if (!filterValue) return true;

  const createdDate = dayjs(row.getValue(columnId));
  const fromDate = dayjs(filterValue.from).subtract(1, "day");
  const toDate = dayjs(filterValue.to).add(1, "day");
  return (
    createdDate.isAfter(fromDate, "day") && createdDate.isBefore(toDate, "day")
  );
};

const columns = [
  {
    id: "select",
    header: ({ table }) => (
      <div className="flex items-center">
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="bg-white border-slate-500"
        />
      </div>
    ),
    cell: ({ row }) => (
      <div className="flex items-center">
        <Checkbox
          onClick={(e) => e.stopPropagation()}
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      </div>
    ),
    size: 28,
    enableSorting: false,
    enableHiding: false,
  },

  {
    id: "invoiceNumber",
    header: "Invoice #",
    accessorKey: "invoice_info.invoice_number",
    cell: ({ row }) => (
      <div className="font-medium">{row.getValue("invoiceNumber")}</div>
    ),
    size: 170,
  },
  {
    id: "invoiceType",
    header: "Type",
    accessorKey: "invoice_info.invoice_type",
    cell: ({ row }) => (
      <div className="font-medium">
        {row.getValue("invoiceType") === "standard" ? "INV" : "QP"}
      </div>
    ),
    size: 170,
  },
  {
    id: "name",
    header: "To",
    accessorFn: (row) => addresseeNamePriority(row),
    size: 220,
    filterFn: multiColumnFilterFn,
    enableHiding: false,
  },
  {
    header: "Email",
    accessorFn: (row) => row.invoice_info?.invoice_to?.email,
    // size: 220,
  },
  {
    header: "Date due",
    accessorFn: (row) => {
      return row.invoice_info.invoice_due_date
        ? dayjs(row.invoice_info.invoice_due_date).format("MM/DD/YYYY")
        : "";
    },
    size: 170,
    sortingFn: (rowA, rowB, columnId) => {
      const a = rowA.original.invoice_info?.invoice_due_date;
      const b = rowB.original.invoice_info?.invoice_due_date;

      if (!a) return -1;
      if (!b) return 1;

      const diff = dayjs(a).diff(b);

      if (diff < 0) return -1;
      if (diff > 0) return 1;
      return 0;
    },
  },
  {
    id: "status",
    header: "Status",
    accessorKey: "invoice_info.invoice_status",
    cell: ({ row }) => <InvoiceStatusBadge status={row.getValue("status")} />,
    size: 170,
    enableSorting: false,
    filterFn: statusFilterFn,
  },
  {
    id: "amount",
    header: "Amount",
    accessorKey: "invoice_info.invoice_total",
    cell: ({ row }) => {
      const amount = parseFloat(row.getValue("amount"));
      const formatted = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(amount);
      return formatted;
    },
    size: 120,
    filterFn: amountFilterFn,
  },
  {
    id: "referenceNumber",
    header: "Reference #",
    accessorKey: "invoice_info.invoice_external_id",
    cell: ({ row }) =>
      row.original.invoice_info.invoice_external_id_link ? (
        <a
          href={formatLink(row.original.invoice_info.invoice_external_id_link)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-700"
          style={{ textDecoration: "underline" }}
        >
          {row.getValue("referenceNumber") || "Click Here"}
        </a>
      ) : (
        row.getValue("referenceNumber") || ""
      ),
    size: 170,
    enableSorting: false,
  },
  {
    header: "Date created",
    accessorFn: (row) => {
      return row.created_at ? dayjs(row.created_at).format("MM/DD/YYYY") : "";
    },
    // size: 170,
    filterFn: dateCreatedFilterFn,
    sortingFn: (rowA, rowB, columnId) => {
      const a = rowA.original.created_at;
      const b = rowB.original.created_at;

      if (!a) return -1;
      if (!b) return 1;

      const diff = dayjs(a).diff(b);

      if (diff < 0) return -1;
      if (diff > 0) return 1;
      return 0;
    },
  },
  {
    header: "Date sent",
    accessorFn: (row) => {
      return row.invoice_info.invoice_send_date
        ? dayjs(row.invoice_info.invoice_send_date).format("MM/DD/YYYY")
        : "";
    },
    // size: 170,
    sortingFn: (rowA, rowB, columnId) => {
      const a = rowA.original.invoice_info?.invoice_send_date;
      const b = rowB.original.invoice_info?.invoice_send_date;

      if (!a) return -1;
      if (!b) return 1;

      const diff = dayjs(a).diff(b);

      if (diff < 0) return -1;
      if (diff > 0) return 1;
      return 0;
    },
  },
  {
    id: "@/actions/",
    header: () => <span className="sr-only">Actions</span>,
    cell: ({ row }) => (
      <div onClick={(e) => e.stopPropagation()}>
        <RowActions row={row} />
      </div>
    ),
    // size: 60,
    enableHiding: false,
  },
];
const fallbackData = [];

export default function InvoiceTable() {
  const { data, isLoading } = useInvoices();
  const { handleOpen: openInvoiceDrawer } = useInvoiceDrawer();
  const { handleOpen: openQuickPayViewDrawer } = useQuickPayViewDrawer();
  const { handleOpen: openQuickPayCreateDrawer } = useQuickPayCreateDrawer();
  const history = useHistory();
  const id = useId();
  const tableRef = useRef(null);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({
    Email: false,
    "Date created": false,
    "Date sent": false,
  });
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const inputRef = useRef(null);

  const [sorting, setSorting] = useState([]);

  const table = useReactTable({
    data: data || fallbackData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    state: {
      sorting,
      pagination,
      columnFilters,
      columnVisibility,
    },
  });

  // Get unique status values
  const uniqueStatusValues = useMemo(() => {
    const statusColumn = table.getColumn("status");

    if (!statusColumn) return [];

    const values = Array.from(statusColumn.getFacetedUniqueValues().keys());

    return values.sort();
  }, [table.getColumn("status")?.getFacetedUniqueValues()]);

  // Get counts for each status
  const statusCounts = useMemo(() => {
    const statusColumn = table.getColumn("status");
    if (!statusColumn) return new Map();
    return statusColumn.getFacetedUniqueValues();
  }, [table.getColumn("status")?.getFacetedUniqueValues()]);

  const selectedStatuses = useMemo(() => {
    const filterValue = table.getColumn("status")?.getFilterValue();
    return filterValue ?? [];
  }, [table.getColumn("status")?.getFilterValue()]);

  const handleStatusChange = (checked, value) => {
    const filterValue = table.getColumn("status")?.getFilterValue();
    const newFilterValue = filterValue ? [...filterValue] : [];

    if (checked) {
      newFilterValue.push(value);
    } else {
      const index = newFilterValue.indexOf(value);
      if (index > -1) {
        newFilterValue.splice(index, 1);
      }
    }

    table
      .getColumn("status")
      ?.setFilterValue(newFilterValue.length ? newFilterValue : undefined);
  };

  const minAmount = useMemo(() => {
    return data?.reduce(
      (acc, invoice) =>
        acc < invoice.invoice_info.invoice_total
          ? acc
          : invoice.invoice_info.invoice_total,
      Infinity
    );
  }, [data]);

  const maxAmount = useMemo(() => {
    return data?.reduce(
      (acc, invoice) =>
        acc > invoice.invoice_info.invoice_total
          ? acc
          : invoice.invoice_info.invoice_total,
      0
    );
  }, [data]);

  useEffect(() => {
    const filterValue = table.getColumn("amount")?.getFilterValue();
    if (!filterValue || filterValue[0] === undefined) {
      table.getColumn("amount")?.setFilterValue([minAmount, maxAmount]);
    }
  }, [data, minAmount, maxAmount]);

  const handleCreatedDateRangeChange = (range) => {
    table.getColumn("Date created")?.setFilterValue(range);
  };

  const formatAmount = (price) => {
    if (!price) return `$${Number("0").toLocaleString()}`;
    return price === maxAmount
      ? `$${price.toLocaleString()}+`
      : `$${price.toLocaleString()}`;
  };

  return (
    <div className="space-y-4 mb-96">
      {/* Filters */}
      <div className="flex flex-wrap xl:flex-nowrap items-center justify-between gap-3 flex-col-reverse lg:flex-row">
        <div className="flex items-center gap-3 flex-col lg:flex-row">
          {/* Filter by name or email */}
          <SearchInput
            searchValue={table.getColumn("name")?.getFilterValue()}
            setSearchValue={table.getColumn("name")?.setFilterValue}
          />
          <div className="flex items-center gap-3 mr-3">
            {/* Filters */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="">
                  <Filter
                    className="-ms-1 me-2 opacity-60"
                    size={16}
                    strokeWidth={2}
                    aria-hidden="true"
                  />
                  Filters
                  {columnFilters.length > 0 && (
                    <span className="-me-1 ms-3 inline-flex h-5 max-h-full items-center rounded border border-border bg-background px-1 font-[inherit] text-[0.625rem] font-medium text-muted-foreground/70">
                      {columnFilters.length}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="min-w-[440px] p-3 py-0" align="start">
                <div className="flex justify-between items-center my-2">
                  <div className="text-normal font-medium text-slate-800">
                    Filters
                  </div>
                  {/* <div className="text-xs font-medium text-muted-foreground"></div> */}
                  <Button
                    variant="link"
                    onClick={() =>
                      table.setColumnFilters([
                        { id: "amount", value: [minAmount, maxAmount] },
                      ])
                    }
                  >
                    Clear all
                  </Button>
                </div>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value={"status"} className="py-2 border-t">
                    <AccordionTrigger className="py-2 hover:no-underline text-sm font-medium text-muted-foreground">
                      Status
                    </AccordionTrigger>
                    <AccordionContent className="py-2 text-muted-foreground">
                      <div className="space-y-3">
                        {uniqueStatusValues.map((value, i) => (
                          <div key={value} className="flex items-center gap-2">
                            <Checkbox
                              id={`${id}-${i}`}
                              checked={selectedStatuses.includes(value)}
                              onCheckedChange={(checked) =>
                                handleStatusChange(checked, value)
                              }
                            />
                            <Label
                              htmlFor={`${id}-${i}`}
                              className="flex grow justify-between gap-2 font-normal"
                            >
                              {value
                                .split("_")
                                .map(
                                  (word) =>
                                    word.charAt(0).toUpperCase() +
                                    word.slice(1).toLowerCase()
                                )
                                .join(" ")}{" "}
                              <span className="ms-2 text-xs text-muted-foreground">
                                {statusCounts.get(value)}
                              </span>
                            </Label>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value={"dateCreated"} className="py-2">
                    <AccordionTrigger className="py-2 hover:no-underline text-sm font-medium text-muted-foreground">
                      Date created
                    </AccordionTrigger>
                    <AccordionContent className="py-2 text-muted-foreground">
                      <CreatedDateRangeFilter
                        date={table.getColumn("Date created")?.getFilterValue()}
                        setDate={handleCreatedDateRangeChange}
                      />
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value={"amount"} className="py-2">
                    <AccordionTrigger className="py-2 hover:no-underline text-sm font-medium text-muted-foreground">
                      Amount
                    </AccordionTrigger>
                    <AccordionContent className="py-2 text-muted-foreground">
                      <div className="space-y-3">
                        <Label className="tabular-nums">
                          From{" "}
                          {formatAmount(
                            table.getColumn("amount")?.getFilterValue()?.[0]
                          )}{" "}
                          to{" "}
                          {formatAmount(
                            table.getColumn("amount")?.getFilterValue()?.[1]
                          )}
                        </Label>
                        <div className="flex items-center gap-4">
                          {minAmount !== undefined &&
                            maxAmount !== undefined && (
                              <Slider
                                value={table
                                  .getColumn("amount")
                                  ?.getFilterValue()}
                                onValueChange={(value) =>
                                  table
                                    .getColumn("amount")
                                    ?.setFilterValue(value)
                                }
                                min={minAmount}
                                max={maxAmount}
                                minStepsBetweenThumbs={20}
                                aria-label="Amount range slider"
                              />
                            )}
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </PopoverContent>
            </Popover>
            {/* Toggle columns visibility */}
            <TableColumnsManager table={table} />
          </div>
        </div>

        {/* Delete button */}
        {/* {table.getSelectedRowModel().rows.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button className="ml-auto" variant="outline">
                  <Trash
                    className="-ms-1 me-2 opacity-60"
                    size={16}
                    strokeWidth={2}
                    aria-hidden="true"
                  />
                  Delete
                  <span className="-me-1 ms-3 inline-flex h-5 max-h-full items-center rounded border border-border bg-background px-1 font-[inherit] text-[0.625rem] font-medium text-muted-foreground/70">
                    {table.getSelectedRowModel().rows.length}
                  </span>
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <div className="flex flex-col gap-2 max-sm:items-center sm:flex-row sm:gap-4">
                  <div
                    className="flex size-9 shrink-0 items-center justify-center rounded-full border border-border"
                    aria-hidden="true"
                  >
                    <AlertCircle
                      className="opacity-80"
                      size={16}
                      strokeWidth={2}
                    />
                  </div>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Are you absolutely sure?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete{" "}
                      {table.getSelectedRowModel().rows.length} selected{" "}
                      {table.getSelectedRowModel().rows.length === 1
                        ? "row"
                        : "rows"}
                      .
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteRows}>
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )} */}

        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto items-center">
          <TableActions table={table} />
          <div className="flex items-center gap-3">
            <Button
              className="bg-green-600 hover:bg-green-600/80 text-white"
              onClick={() => history.push("/invoices/new")}
            >
              <Plus
                className="-ms-1 me-2"
                size={16}
                strokeWidth={2}
                aria-hidden="true"
              />
              Create Invoice
            </Button>
            {/* <Button
              className="bg-green-600 hover:bg-green-600/80 text-white"
              onClick={() => openQuickPayCreateDrawer()}
            >
              <Plus
                className="-ms-1 me-2"
                size={16}
                strokeWidth={2}
                aria-hidden="true"
              />
              New Quick Pay
            </Button> */}
          </div>
        </div>
      </div>
      <MainTable
        ref={tableRef}
        table={table}
        isLoading={isLoading}
        onRowClick={(invoice) => {
          if (invoice.invoice_info?.invoice_type === "quickpay") {
            openQuickPayViewDrawer(invoice.invoice_id);
          } else {
            openInvoiceDrawer(invoice.invoice_id);
          }
        }}
      />
      <TablePagination table={table} />
      <DeleteInvoiceAlert />
      <QuickPayDrawer />
    </div>
  );
}

function RowActions({ row }) {
  const { user } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const invoice = row.original;
  const [cancelPaymentOpen, setCancelPaymentOpen] = useState(false);

  const isCancelable =
    [PENDING_INVOICE_STATUS, PROCESSING_INVOICE_STATUS].includes(
      invoice?.invoice_info?.invoice_status,
    ) &&
    invoice?.invoice_info?.invoice_payment_method_type?.toLowerCase() ===
      "credit_card";

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="flex justify-end">
          <Button
            size="icon"
            variant="ghost"
            className="shadow-none"
            aria-label="Edit item"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-ellipsis"
            >
              <circle cx="12" cy="12" r="1" />
              <circle cx="19" cy="12" r="1" />
              <circle cx="5" cy="12" r="1" />
            </svg>
          </Button>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {!hasPermission(user, "invoice", "update", invoice) &&
          !hasPermission(user, "invoice", "delete", invoice) &&
          !isCancelable && (
            <div className="text-sm text-slate-700 p-2">No actions</div>
          )}
        <DropdownMenuGroup>
          {hasPermission(user, "invoice", "update", invoice) && (
            <DropdownMenuItem
              onClick={(e) => {
                // e.stopPropagation();
                history.push(`/invoices/edit/${invoice?.invoice_id}`);
              }}
            >
              <span>Edit</span>
            </DropdownMenuItem>
          )}
          {/* <DropdownMenuItem>
            <span>Duplicate</span>
          </DropdownMenuItem> */}
        </DropdownMenuGroup>
        {hasPermission(user, "invoice", "delete", invoice) && (
          <>
            <DropdownMenuSeparator />

            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={(e) => {
                //   e.preventDefault();
                if (queryParams.has("delete_invoice")) {
                  queryParams.delete("delete_invoice");
                  history.replace({
                    search: queryParams.toString(),
                  });
                } else {
                  queryParams.set("delete_invoice", invoice?.invoice_id);
                  history.replace({
                    search: queryParams.toString(),
                  });
                }
              }}
            >
              <span>Delete</span>
            </DropdownMenuItem>
          </>
        )}
        {isCancelable && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-red-600 focus:text-red-600"
              onClick={() => setCancelPaymentOpen(true)}
            >
              <span>Cancel Payment</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
    <CancelPaymentModal
      open={cancelPaymentOpen}
      onOpenChange={setCancelPaymentOpen}
      invoice={invoice}
      onSuccess={() => setCancelPaymentOpen(false)}
    />
    </>
  );
}

function TableActions({ table }) {
  const queryClient = useQueryClient();
  const { handleOpen: openMRDC } = useCheckDepositDrawer();
  const { user } = useAuth();
  const [selectRowsAlertOpen, setSelectRowsAlertOpen] = useState(false);
  const [bulkSendDialogOpen, setBulkSendDialogOpen] = useState(false);
  const { mutate: bulkSend, isLoading: isBulkSending } = useBulkSend();

  const invoices = table
    .getPrePaginationRowModel()
    .flatRows.map((row) => row.original);

  const handleBulkSend = () => {
    const selectedRows = table.getSelectedRowModel().rows;

    if (selectedRows.length <= 0) {
      return setSelectRowsAlertOpen(true);
    }

    setBulkSendDialogOpen(true);
  };

  const rowsToSend = useMemo(() => {
    const selectedRows = table.getSelectedRowModel().rows;
    const filteredRows = selectedRows.filter(
      (row) => row.original.invoice_info.invoice_status === "draft"
    );

    return filteredRows;
  }, [table.getSelectedRowModel().rows]);

  const sendInvoices = () => {
    bulkSend(
      rowsToSend.map((row) => row.original.invoice_id),
      {
        onSuccess: (data) => {
          if (data.failed_invoices.length === 0) {
            toast.success("All invoices have been sent.", {
              autoClose: 6000,
            });
          } else if (data?.failed_invoices.length > 0) {
            toast.warn(
              `${data.failed_invoices.length} invoice${
                data.failed_invoices.length === 1 ? "" : "s"
              } failed to send. Please check if all required information was provided.`,
              {
                autoClose: 10000,
              }
            );
          }
        },
        onError: (error) => {
          if (error instanceof AxiosError && error.code === 504) {
            toast.success("Invoices have been sent.", {
              autoClose: 6000,
            });
          }
          toast.error("Something went wrong while sending invoices.");
        },
        onSettled: () => {
          setBulkSendDialogOpen(false);
          table.resetRowSelection();
          queryClient.invalidateQueries(invoiceQueryKeys.all);
        },
      }
    );
  };

  const mapInvoiceForExport = (invoice) => {
    let res = {};
    if (!invoice) return res;
    const flatten = (source, prefix = "", exclude = []) => {
      if (!source) return;
      for (const [key, value] of Object.entries(source)) {
        if (
          (!key.includes("id") ||
            key === "invoice_external_id" ||
            key === "contact_ref_id") &&
          !exclude.includes(key)
        ) {
          res[prefix ? `${prefix}_${key}` : key] = value;
        }
      }
    };

    flatten(invoice.invoice_info?.invoice_from, "invoice_from");
    flatten(invoice.invoice_info?.invoice_to, "invoice_to");
    flatten(invoice.invoice_info, "", [
      "invoice_from",
      "invoice_to",
      "invoice_line_items",
      "invoice_full_location",
    ]);
    flatten(invoice, "", ["invoice_info"]);

    return res;
  };

  const exportTable = () => {
    const selectedRows = table.getSelectedRowModel().rows;
    let data = invoices;

    if (selectedRows && selectedRows.length) {
      data = selectedRows.map((row) => row.original);
    }

    const content = data.map((item) => mapInvoiceForExport(item));
    exportToCsv(content || [], [], "Invoices");
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline">
            Actions
            <ChevronDown
              className="-me-1 ms-2 opacity-60"
              size={16}
              strokeWidth={2}
              aria-hidden="true"
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {/* {table.getSelectedRowModel().rows.length > 0 && ( */}
          <DropdownMenuItem onClick={() => handleBulkSend()}>
            <div className="flex gap-x-2 ">
              <Send className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium">
                  Send multiple invoices
                </span>
                <span className="text-xs text-muted-foreground">
                  Bulk send draft invoices.
                </span>
              </div>
            </div>
          </DropdownMenuItem>
          {/* )} */}
          {/* <UploadInvoiceButton>
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            <div className="flex gap-x-2 ">
              <Upload className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium">Upload invoice</span>
                <span className="text-xs text-muted-foreground">
                  Have all your invoices in one place.
                </span>
              </div>
            </div>
          </DropdownMenuItem>
        </UploadInvoiceButton> */}
          {hasPermission(user, "mrdc", "create", "flag") && (
            <DropdownMenuItem onClick={() => openMRDC("true")}>
              <div className="flex gap-x-2 ">
                <Banknote className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-medium">Deposit check</span>
                  <span className="text-xs text-muted-foreground">
                    Deposit check you received as invoice payment.
                  </span>
                </div>
              </div>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={exportTable}>
            <div className="flex gap-x-2 ">
              <Download className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium">Export</span>
                <span className="text-xs text-muted-foreground">
                  Export invoices to a csv file. The filters and sorting will be
                  applied.
                </span>
              </div>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <AlertDialog
        open={selectRowsAlertOpen}
        onOpenChange={(value) => setSelectRowsAlertOpen(value)}
      >
        <AlertDialogContent>
          <div className="flex flex-col gap-2 max-sm:items-center sm:flex-row sm:gap-4">
            <AlertDialogHeader>
              <AlertDialogTitle>No invoice selected!</AlertDialogTitle>
              <AlertDialogDescription>
                To bulk send invoices you must select one or more invoices in
                the table. Note that only invoices in the draft status and that
                have required data will be sent.
              </AlertDialogDescription>
            </AlertDialogHeader>
          </div>
          <AlertDialogFooter>
            <AlertDialogAction>Got it!</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={bulkSendDialogOpen}
        onOpenChange={(value) => {
          if (!isBulkSending) {
            setBulkSendDialogOpen(value);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-x-2">
              <span
                className={
                  "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset  text-green-700 ring-green-600/20 bg-green-50"
                }
              >
                <span className="">
                  {table.getSelectedRowModel().rows.length}
                </span>
              </span>
              Selected
            </DialogTitle>
            <DialogDescription>
              {" "}
              {rowsToSend.length === table.getSelectedRowModel().rows.length ? (
                <span>
                  All selected invoices will be sent. This action cannot be
                  undone.
                </span>
              ) : (
                <span>
                  You are able to send{" "}
                  <span className="font-bold">{rowsToSend.length}</span>{" "}
                  invoices from the {table.getSelectedRowModel().rows.length}{" "}
                  selected. Invoices must have draft status and required data to
                  be sent
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setBulkSendDialogOpen(false)}
              disabled={isBulkSending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={sendInvoices}
              disabled={isBulkSending}
            >
              Send {rowsToSend.length} invoices{" "}
              {isBulkSending && (
                <Loader2 className="w-4 h-4 ml-2 animate-spin" />
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CreatedDateRangeFilter({ date, setDate }) {
  const today = new Date();
  const yesterday = {
    from: subDays(today, 1),
    to: subDays(today, 1),
  };
  const last7Days = {
    from: subDays(today, 6),
    to: today,
  };
  const last30Days = {
    from: subDays(today, 29),
    to: today,
  };
  const monthToDate = {
    from: startOfMonth(today),
    to: today,
  };
  const lastMonth = {
    from: startOfMonth(subMonths(today, 1)),
    to: endOfMonth(subMonths(today, 1)),
  };
  const yearToDate = {
    from: startOfYear(today),
    to: today,
  };
  const lastYear = {
    from: startOfYear(subYears(today, 1)),
    to: endOfYear(subYears(today, 1)),
  };

  const [month, setMonth] = useState(date?.to);

  return (
    <div>
      <div className="rounded-lg border border-border">
        <div className="flex max-sm:flex-col">
          <div className="relative border-border py-4 max-sm:order-1 max-sm:border-t sm:w-36">
            <div className="h-full border-border sm:border-e">
              <div className="flex flex-col px-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setDate({
                      from: today,
                      to: today,
                    });
                    setMonth(today);
                  }}
                >
                  Today
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setDate(yesterday);
                    setMonth(yesterday.to);
                  }}
                >
                  Yesterday
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setDate(last7Days);
                    setMonth(last7Days.to);
                  }}
                >
                  Last 7 days
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setDate(last30Days);
                    setMonth(last30Days.to);
                  }}
                >
                  Last 30 days
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setDate(monthToDate);
                    setMonth(monthToDate.to);
                  }}
                >
                  Month to date
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setDate(lastMonth);
                    setMonth(lastMonth.to);
                  }}
                >
                  Last month
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setDate(yearToDate);
                    setMonth(yearToDate.to);
                  }}
                >
                  Year to date
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setDate(lastYear);
                    setMonth(lastYear.to);
                  }}
                >
                  Last year
                </Button>
              </div>
            </div>
          </div>
          <Calendar
            mode="range"
            selected={date}
            onSelect={(newDate) => {
              if (newDate) {
                setDate(newDate);
              }
            }}
            month={month}
            onMonthChange={setMonth}
            className="p-2"
            disabled={[
              { after: today }, // Dates before today
            ]}
          />
        </div>
      </div>
    </div>
  );
}
